/**
 * Unified Document Management Hook
 * 
 * Handles ALL 12 document types through a single interface using doc_type discriminator:
 * - Sales: invoice, quote, credit_note, proforma, delivery_note
 * - Purchases: purchase_order, expense, grn, supplier_credit
 * - Internal: sales_order, supplier_quote, purchase_requisition
 * 
 * This hook provides type-safe operations for all document types while maintaining
 * a unified codebase. Uses TypeScript discriminated unions for type safety.
 * 
 * @version 2.0.0
 * @since October 2025 - Unified Documents Migration
 */

import { useState, useCallback } from 'react';
import { useFinanceAPI } from './useFinanceAPI';

// ============================================================================
// Document Type Definitions
// ============================================================================

export type DocumentType = 
  | 'invoice' 
  | 'quote' 
  | 'credit_note' 
  | 'proforma' 
  | 'delivery_note'
  | 'purchase_order' 
  | 'expense' 
  | 'grn' 
  | 'supplier_credit'
  | 'sales_order'
  | 'supplier_quote'
  | 'purchase_requisition';

export type DocumentStatus = 
  | 'draft' 
  | 'pending_approval'
  | 'sent' 
  | 'viewed' 
  | 'accepted' 
  | 'declined' 
  | 'approved'
  | 'partial' 
  | 'paid' 
  | 'overdue' 
  | 'cancelled' 
  | 'void'
  | 'expired'
  | 'converted'
  | 'received'
  | 'partially_received'
  | 'closed'
  | 'fulfilled'
  | 'shipped'
  | 'delivered';

// ============================================================================
// Base Document Interface (fields common to ALL documents)
// ============================================================================

export interface BaseDocument {
  id?: number;
  client_id?: number;
  doc_type: DocumentType;
  doc_number: string;
  status: DocumentStatus;
  
  // Dates
  issued_at: string;  // Universal field (was invoice_date, po_date, etc.)
  due_at?: string;    // For invoices, bills
  expiry_date?: string; // For quotes
  
  // Contact (customer or supplier)
  contact_id?: number;
  contact_name?: string;
  contact_email?: string;
  contact_address?: string;
  
  // Financials
  currency: string;
  exchange_rate?: number;
  subtotal: number;
  tax_total: number;
  discount_amount?: number;
  discount_percentage?: number;
  grand_total: number;
  
  // Payment tracking (invoices, bills)
  amount_paid?: number;
  balance_due?: number;
  payment_terms?: number;
  payment_status?: 'unpaid' | 'partial' | 'paid' | 'overdue';
  paid_at?: string;
  
  // Additional info
  notes?: string;
  terms_and_conditions?: string;
  reference?: string;
  
  // Line items
  items?: DocumentLineItem[];
  
  // Metadata
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
  deleted_at?: string;
  
  // Document tracking
  sent_at?: string;
  viewed_at?: string;
}

// ============================================================================
// Purchase-Specific Fields
// ============================================================================

export interface PurchaseDocument extends BaseDocument {
  doc_type: 'purchase_order' | 'expense' | 'grn' | 'supplier_credit' | 'supplier_quote' | 'purchase_requisition';
  
  // Purchase-specific fields
  supplier_reference?: string;  // Vendor invoice number (for bills)
  requisition_id?: number;      // Links to purchase requisitions
  expected_delivery_date?: string;
  delivery_instructions?: string;
  quantity_received_percentage?: number;
  
  // Approval workflow
  approved_by?: number;
  approved_at?: string;
  
  // Accounting integration
  posted_to_accounting?: boolean;
  posted_at?: string;
  
  // GRN linkage
  grn_id?: number;
}

// ============================================================================
// Sales Order-Specific Fields
// ============================================================================

export interface SalesOrderDocument extends BaseDocument {
  doc_type: 'sales_order';
  
  fulfillment_status?: 'pending' | 'partial' | 'fulfilled' | 'cancelled';
  expected_ship_date?: string;
  customer_po_number?: string;
  shipping_method?: string;
  shipping_cost?: number;
  payment_terms_text?: string;
  internal_notes?: string;
  invoice_created?: boolean;
}

// ============================================================================
// Document Type Union (discriminated by doc_type)
// ============================================================================

export type Document = BaseDocument | PurchaseDocument | SalesOrderDocument;

// ============================================================================
// Document Line Item Interface
// ============================================================================

export interface DocumentLineItem {
  id?: number;
  document_id?: number;
  item_id?: number;
  item_code?: string;
  item_name?: string;
  description: string;
  quantity: number;
  unit?: string;  // Unit of measure (pcs, kg, hours, etc.)
  unit_price: number;
  discount_amount?: number;
  discount_percentage?: number;
  line_total: number;
  tax_rate_id?: number;
  tax_rate: number;
  tax_amount: number;
  sort_order: number;
  
  // Category tracking (for material auto-suggestions)
  category_id?: number | null;
  category_name?: string | null;
  
  // Quantity tracking (for POs, GRNs, etc.)
  quantity_ordered?: number;
  quantity_received?: number;
  quantity_billed?: number;
  quantity_shipped?: number;
}

// ============================================================================
// Filter Interface
// ============================================================================

export interface DocumentFilters {
  doc_type?: DocumentType | DocumentType[];
  status?: DocumentStatus | DocumentStatus[];
  contact_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  payment_status?: 'unpaid' | 'partial' | 'paid' | 'overdue';
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Document Number Prefixes
// ============================================================================

const DOC_NUMBER_PREFIXES: Record<DocumentType, string> = {
  invoice: 'INV',
  quote: 'QUO',
  credit_note: 'CN',
  proforma: 'PRO',
  delivery_note: 'DN',
  purchase_order: 'PO',
  expense: 'EXP',
  grn: 'GRN',
  supplier_credit: 'SC',
  sales_order: 'SO',
  supplier_quote: 'SQ',
  purchase_requisition: 'PR'
};

// ============================================================================
// Unified Documents Hook
// ============================================================================

export function useDocuments(defaultDocType?: DocumentType) {
  const api = useFinanceAPI();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 25,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  /**
   * Load documents with optional filters
   * Supports filtering by single or multiple doc_types
   */
  const loadDocuments = useCallback(async (filters?: DocumentFilters) => {
    const where: any = {};
    
    // Handle doc_type filter
    if (filters?.doc_type) {
      where.doc_type = filters.doc_type;
    } else if (defaultDocType) {
      where.doc_type = defaultDocType;
    }
    
    // Handle status filter (skip if 'all' or empty)
    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        where.status = filters.status;
      } else if (filters.status !== 'all' as any) {
        where.status = filters.status;
      }
    }
    
    if (filters?.contact_id) {
      where.contact_id = filters.contact_id;
    }
    
    if (filters?.payment_status) {
      where.payment_status = filters.payment_status;
    }

    const result = await api.list<Document>('work_items', {
      where,
      search: filters?.search,
      orderBy: [{ field: 'issued_at', direction: 'desc' }],
      page: filters?.page || 1,
      pageSize: filters?.pageSize || 25
    });

    if (result) {
      // Add backward compatibility aliases
      const documentsWithAliases = (result.data || []).map(doc => addBackwardCompatibilityAliases(doc));
      setDocuments(documentsWithAliases);
      
      if (result.pagination) {
        setPagination(result.pagination);
      }
    } else {
      setDocuments([]);
    }

    return result;
  }, [api, defaultDocType]);

  /**
   * Load a single document by ID
   */
  const loadDocument = useCallback(async (id: number, docType?: DocumentType) => {
    const where: any = { id };
    if (docType) {
      where.doc_type = docType;
    }
    
    const result = await api.callAPI<{ data: Document }>({
      action: 'read',
      table: 'work_items',
      where
    });
    
    const document = result?.data || null;
    if (document) {
      const docWithAliases = addBackwardCompatibilityAliases(document);
      setCurrentDocument(docWithAliases);
      return docWithAliases;
    }
    return null;
  }, [api]);

  /**
   * Load line items for a document
   */
  const loadDocumentItems = useCallback(async (documentId: number) => {
    return api.list<DocumentLineItem>('work_item_bom_items', {
      where: { work_item_id: documentId },
      orderBy: [{ field: 'line_no', direction: 'asc' }]
    });
  }, [api]);

  /**
   * Create a new document
   */
  const createDocument = useCallback(async (document: Partial<Document>) => {
    const { items, ...documentData } = document as any;
    
    console.log('📝 Creating document with items:', { itemCount: items?.length, items });
    
    // Ensure doc_type is set
    if (!documentData.doc_type) {
      documentData.doc_type = defaultDocType;
    }
    
    // Map doc_type to item_type for work_items table
    documentData.item_type = documentData.doc_type;
    
    // Note: doc_number is auto-generated by backend if not provided
    const createdDocument = await api.create<Document>('work_items', documentData);
    
    // Create line items if provided
    if (createdDocument && createdDocument.id && items && items.length > 0) {
      console.log('💾 Saving', items.length, 'line items for document', createdDocument.id);
      
      // ⚠️ CRITICAL FIX: createdDocument.id comes back as string from API, but DB expects integer
      const documentIdAsNumber = parseInt(createdDocument.id as any, 10);
      console.log('  Document ID type conversion:', typeof createdDocument.id, '→', typeof documentIdAsNumber);
      
      for (let i = 0; i < items.length; i++) {
        const lineItem = {
          work_item_id: documentIdAsNumber,  // ✅ Use number instead of string
          line_no: i + 1,
          description: items[i].description,
          quantity: items[i].quantity,
          unit_price: items[i].unit_price,
          tax_amount: items[i].tax_amount || 0,
          discount_amount: items[i].discount_amount || 0,
          discount_percentage: items[i].discount_percentage || 0,
          item_id: items[i].item_id,
          item_code: items[i].item_code,
          tax_rate_id: items[i].tax_rate_id,
          unit: items[i].unit || 'unit'
        };
        console.log('  Line', i + 1, ':', lineItem);
        
        try {
          const result = await api.create('work_item_bom_items', lineItem);
          console.log('  ✅ Line', i + 1, 'saved successfully:', result?.id);
        } catch (error: any) {
          console.error('  ❌ Failed to save line', i + 1, ':', error.message);
          throw error;  // Re-throw to stop processing and show error to user
        }
      }
      
      // 🔄 CRITICAL: Reload line items from database and recalculate totals
      console.log('🧮 Reloading line items from database...');
      const savedItemsResponse = await loadDocumentItems(documentIdAsNumber);
      const savedItems = savedItemsResponse?.data || [];
      
      // Filter out empty/invalid line items (where unit_price <= 0 or description is empty)
      const validItems = savedItems.filter(item => {
        const unitPrice = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : item.unit_price;
        return unitPrice > 0 && item.description && item.description.trim() !== '';
      });
      
      if (validItems && validItems.length > 0) {
        console.log('📊 Calculating totals from', validItems.length, 'valid line items (filtered from', savedItems.length, 'total)');
        const totalsResult = await api.callAPI<{ totals: any }>({
          action: 'calculateTotals',
          data: { items: validItems }
        });
        
        if (totalsResult?.totals) {
          console.log('💰 Updating document with calculated totals:', totalsResult.totals);
          await api.update('work_items', { id: documentIdAsNumber }, {
            subtotal: totalsResult.totals.subtotal,
            tax_total: totalsResult.totals.total_tax,
            grand_total: totalsResult.totals.total,
            discount_amount: totalsResult.totals.total_discount || 0
          });
          console.log('✅ Document totals updated successfully');
        }
      }
      
      // Reload to get fresh data with items
      await loadDocuments({ doc_type: documentData.doc_type as DocumentType });
    }
    
    return createdDocument;
  }, [api, defaultDocType, loadDocuments, loadDocumentItems]);

  /**
   * Update an existing document
   */
  const updateDocument = useCallback(async (id: number, document: Partial<Document>) => {
    const { items, ...documentData } = document;
    
    const updatedDocument = await api.update('work_items', { id }, documentData);
    
    // Update line items if provided
    if (updatedDocument && items !== undefined) {
      // Delete existing items
      // Delete old line items and create new ones
      await api.callAPI({
        action: 'delete',
        table: 'work_item_bom_items',
        where: { work_item_id: id }
      });
      
      // Create new items
      for (let i = 0; i < items.length; i++) {
        await api.create('work_item_bom_items', {
          work_item_id: id,
          line_no: i + 1,
          description: items[i].description,
          quantity: items[i].quantity,
          unit_price: items[i].unit_price,
          tax_amount: items[i].tax_amount || 0,
          discount_amount: items[i].discount_amount || 0,
          discount_percentage: items[i].discount_percentage || 0,
          item_id: items[i].item_id,
          item_code: items[i].item_code,
          tax_rate_id: items[i].tax_rate_id,
          unit: items[i].unit || 'unit'
        });
      }
    }
    
    await loadDocuments({ doc_type: document.doc_type });
    
    return updatedDocument;
  }, [api, loadDocuments]);

  /**
   * Delete a document (soft delete)
   */
  const deleteDocument = useCallback(async (id: number, docType?: DocumentType) => {
    const result = await api.callAPI({
      action: 'delete',
      table: 'work_items',
      where: { id }
    });
    if (result) {
      await loadDocuments({ doc_type: docType || defaultDocType });
    }
    return result;
  }, [api, defaultDocType, loadDocuments]);

  /**
   * Generate document number
   * Note: The backend auto-generates doc_number on create, so this is mainly for preview
   */
  const generateDocumentNumber = useCallback(async (docType: DocumentType): Promise<string> => {
    const result = await api.callAPI<{ doc_number: string }>({
      action: 'generateDocumentNumber',
      data: { doc_type: docType }
    });
    
    if (result && result.doc_number) {
      return result.doc_number;
    }
    
    // Fallback: generate client-side
    const prefix = DOC_NUMBER_PREFIXES[docType];
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${timestamp}`;
  }, [api]);

  /**
   * Calculate document totals from line items
   */
  const calculateTotals = useCallback(async (items: DocumentLineItem[]) => {
    return api.calculateTotals(items);
  }, [api]);

  /**
   * Update document status
   */
  const updateStatus = useCallback(async (id: number, status: DocumentStatus, docType?: DocumentType) => {
    const result = await api.update('work_items', { id }, { status });
    if (result) {
      await loadDocuments({ doc_type: docType || defaultDocType });
    }
    return result;
  }, [api, defaultDocType, loadDocuments]);

  /**
   * Send document via email
   */
  const sendDocument = useCallback(async (id: number, email?: string, docType?: DocumentType) => {
    const result = await api.callAPI({
      action: 'sendDocument',
      data: { document_id: id, doc_type: docType || defaultDocType, email }
    });
    
    if (result.success) {
      await updateStatus(id, 'sent', docType);
    }
    return result;
  }, [api, defaultDocType, updateStatus]);

  /**
   * Convert document to another type
   * E.g., quote → invoice, purchase_order → grn
   */
  const convertDocument = useCallback(async (
    sourceId: number,
    targetDocType: DocumentType,
    sourceDocType?: DocumentType,
    conversionData?: any
  ) => {
    const result = await api.callAPI<{
      success: boolean;
      target_id?: number;
      target_number?: string;
      target_document?: Document;
      error?: string;
    }>({
      action: 'convertDocument',
      data: { 
        source_id: sourceId,
        source_type: sourceDocType || defaultDocType,
        target_type: targetDocType,
        conversion_data: conversionData
      }
    });
    
    if (result && result.success) {
      // Reload both source and target document types
      await loadDocuments({ doc_type: sourceDocType || defaultDocType });
      if (targetDocType !== (sourceDocType || defaultDocType)) {
        await loadDocuments({ doc_type: targetDocType });
      }
    }
    return result;
  }, [api, defaultDocType, loadDocuments]);

  /**
   * Mark invoice/expense as paid
   */
  const markAsPaid = useCallback(async (id: number, docType: 'invoice' | 'expense') => {
    const result = await api.callAPI({
      action: 'markDocumentAsPaid',
      data: { document_id: id, doc_type: docType }
    });
    
    if (result.success) {
      await loadDocuments({ doc_type: docType });
    }
    return result;
  }, [api, loadDocuments]);

  /**
   * Void document (for invoices, credit notes)
   */
  const voidDocument = useCallback(async (id: number, docType: DocumentType) => {
    return updateStatus(id, 'void', docType);
  }, [updateStatus]);

  /**
   * Get document type display name
   */
  const getDocumentTypeName = useCallback((docType: DocumentType): string => {
    const names: Record<DocumentType, string> = {
      invoice: 'Invoice',
      quote: 'Quote',
      credit_note: 'Credit Note',
      proforma: 'Proforma Invoice',
      delivery_note: 'Delivery Note',
      purchase_order: 'Purchase Order',
      expense: 'Expense',
      grn: 'Goods Received Note',
      supplier_credit: 'Supplier Credit',
      sales_order: 'Sales Order',
      supplier_quote: 'Supplier Quote',
      purchase_requisition: 'Purchase Requisition'
    };
    return names[docType];
  }, []);

  return {
    // State
    documents,
    currentDocument,
    pagination,
    loading: api.loading,
    error: api.error,
    
    // Core CRUD
    loadDocuments,
    loadDocument,
    loadDocumentItems,
    createDocument,
    updateDocument,
    deleteDocument,
    
    // Utilities
    generateDocumentNumber,
    calculateTotals,
    getDocumentTypeName,
    
    // Actions
    updateStatus,
    sendDocument,
    convertDocument,
    markAsPaid,
    voidDocument
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Add backward compatibility aliases for old field names
 * This ensures existing components don't break during migration
 */
function addBackwardCompatibilityAliases(doc: Document): any {
  const aliases: any = { ...doc };
  
  // Field name aliases (old → new)
  aliases.invoice_number = doc.doc_number;
  aliases.quote_number = doc.doc_number;
  aliases.po_number = doc.doc_number;
  aliases.bill_number = doc.doc_number;
  aliases.credit_note_number = doc.doc_number;
  aliases.delivery_note_number = doc.doc_number;
  aliases.grn_number = doc.doc_number;
  aliases.sales_order_number = doc.doc_number;
  
  aliases.invoice_date = doc.issued_at;
  aliases.quote_date = doc.issued_at;
  aliases.po_date = doc.issued_at;
  aliases.bill_date = doc.issued_at;
  aliases.credit_date = doc.issued_at;
  aliases.order_date = doc.issued_at;
  aliases.received_date = doc.issued_at;
  
  aliases.total_amount = doc.grand_total;
  aliases.subtotal_amount = doc.subtotal;
  aliases.tax_amount = doc.tax_total;
  
  aliases.due_date = doc.due_at;
  
  return aliases;
}

/**
 * Type guard to check if document is a purchase document
 */
export function isPurchaseDocument(doc: Document): doc is PurchaseDocument {
  return ['purchase_order', 'expense', 'grn', 'supplier_credit', 'supplier_quote', 'purchase_requisition'].includes(doc.doc_type);
}

/**
 * Type guard to check if document is a sales document
 */
export function isSalesDocument(doc: Document): doc is BaseDocument {
  return ['invoice', 'quote', 'credit_note', 'proforma', 'delivery_note'].includes(doc.doc_type);
}

/**
 * Type guard to check if document is a sales order
 */
export function isSalesOrder(doc: Document): doc is SalesOrderDocument {
  return doc.doc_type === 'sales_order';
}
